import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  ClipboardCheck,
  Layers,
  Users,
  Boxes,
  Settings,
  FileCheck,
  ShieldCheck,
  Shield,
  Trash2,
  Building2,
  Inbox,
  Wrench,
  Cpu,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Database,
  Cog,
  FolderKanban,
  ListChecks,
  Target,
  KeyRound,
  CheckCircle2,
  FlaskConical,
  CalendarClock,
  ReceiptText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AlertsBell from "@/components/AlertsBell";

const STORAGE_KEY = "aampl_npd_sidebar_collapsed";

// Routes grouped under the "RFQ & Costing" collapsible parent.
const RFQ_COSTING_PATHS = ["/rfqs", "/npd", "/assemblies", "/cost-breakdown"];

// Routes that live under the "Masters" group (used to auto-open it on those pages).
const MASTER_PATHS = ["/customers", "/machines", "/materials", "/operations", "/apqp-master", "/roles", "/audit", "/settings", "/tnc"];
// Routes under the "Administration" group.
const ADMIN_PATHS = ["/units", "/users", "/admin/api-keys", "/admin/rfq-orphans", "/admin/hr-export", "/trash"];

function getInitialCollapsed() {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored !== null) return stored === "true";
  // No stored preference yet — default based on viewport width.
  return window.innerWidth < 1024;
}

export default function Sidebar() {
  const { user, hasPermission, canSee } = useAuth();
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [mastersOpen, setMastersOpen] = useState(() =>
    MASTER_PATHS.some((p) => (typeof window !== "undefined" ? window.location.pathname : "").startsWith(p))
  );
  const [adminOpen, setAdminOpen] = useState(() =>
    ADMIN_PATHS.some((p) => (typeof window !== "undefined" ? window.location.pathname : "").startsWith(p))
  );
  const [rfqCostingOpen, setRfqCostingOpen] = useState(() =>
    RFQ_COSTING_PATHS.some((p) => (typeof window !== "undefined" ? window.location.pathname : "").startsWith(p))
  );
  const [apqpOpen, setApqpOpen] = useState(() =>
    ["/apqp"].some((p) => (typeof window !== "undefined" ? window.location.pathname : "").startsWith(p))
  );
  const location = useLocation();

  // Under-Approval queue count → sidebar badge. Refresh on navigation so it drops after approvals.
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const canApproveNpdEarly = hasPermission("npd.approve");
  const canViewApprovedEarly = hasPermission("view_approved") || hasPermission("npd.approve");
  useEffect(() => {
    if (!canApproveNpdEarly) return;
    let alive = true;
    api.get("/npd", { params: { pending_approval: "yes", limit: 1 } })
      .then((r) => { if (alive) setPendingCount(r.data?.total || 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canApproveNpdEarly, location.pathname]);
  useEffect(() => {
    if (!canViewApprovedEarly) return;
    let alive = true;
    api.get("/npd", { params: { approved: "yes", limit: 1 } })
      .then((r) => { if (alive) setApprovedCount(r.data?.total || 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canViewApprovedEarly, location.pathname]);

  // Persist user choice across reloads.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // Keep a group open whenever the active page lives inside it.
  useEffect(() => {
    if (MASTER_PATHS.some((p) => location.pathname.startsWith(p))) setMastersOpen(true);
    if (ADMIN_PATHS.some((p) => location.pathname.startsWith(p))) setAdminOpen(true);
    if (RFQ_COSTING_PATHS.some((p) => location.pathname.startsWith(p))) setRfqCostingOpen(true);
  }, [location.pathname]);

  // Auto-collapse once whenever the window crosses below 1024px. Do not
  // continuously force collapse — the user can still expand on a narrow
  // screen if they want to.
  useEffect(() => {
    let wasNarrow = window.innerWidth < 1024;
    const handleResize = () => {
      const isNarrow = window.innerWidth < 1024;
      if (isNarrow && !wasNarrow) setCollapsed(true);
      wasNarrow = isNarrow;
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!user) return null;

  // Round 21 — permission-based sidebar gates (unchanged).
  const canManageUsers = hasPermission("manage_users");
  const canManageUnits = hasPermission("manage_units");
  const canManageRoles = hasPermission("manage_roles");
  const canManageApiKeys = user.role === "admin" || hasPermission("manage_api_keys");
  // HR monthly export sits at the same level as Backup & Restore.
  const canHrExport = user.role === "admin" || hasPermission("backup_restore");
  const canEditSettings = hasPermission("edit_settings");
  const canEditProcesses = hasPermission("edit_process_master");
  const canViewRbacAudit = hasPermission("view_rbac_audit");
  const canDeleteNpd = hasPermission("npd.delete");
  const canManageCustomers = hasPermission("manage_customers");
  const canViewReports = hasPermission("view_reports");
  const canApproveNpd = hasPermission("npd.approve");
  // APQP-only role: sidebar shows just the APQP box, nothing else.
  const apqpOnly = !!user?.apqp_own_tasks_only && user?.role !== "admin";

  // Opt-in section visibility (canSee from AuthContext). For open roles and admins every
  // section shows (unchanged); a restricted role only sees the sections it was granted.
  const showNpd = canSee("npd.view");
  const showAssembly = canSee("assembly.view");
  const showCostBreakdown = user.role === "admin" || hasPermission("view_cost_breakdown");
  const showCosting = showNpd || showAssembly || showCostBreakdown;
  const showMaterials = canSee("material.view");
  const showOperations = canSee("operation.view");
  const showMasters =
    canManageCustomers || canEditSettings || canEditProcesses ||
    showMaterials || showOperations || canManageRoles || canViewRbacAudit;

  // Both "NPD Costings" and "Under Approval" point at /npd, so NavLink's path-only
  // active state can't tell them apart. Distinguish on the ?pending_approval query.
  const onNpd = location.pathname === "/npd";
  const _npdParams = new URLSearchParams(location.search);
  const pendingActive = onNpd && _npdParams.get("pending_approval") === "yes";
  const approvedActive = onNpd && _npdParams.get("approved") === "yes";
  const canViewApproved = user?.role === "admin" || hasPermission("view_approved") || hasPermission("npd.approve");

  // Administration section holds Units / Users / API Keys / RFQ Orphans / Trash
  // (Roles, RBAC Audit, Settings, T&C moved into the Masters group below).
  const showAdminSection =
    canManageUsers || canManageUnits || canDeleteNpd || canManageApiKeys || canHrExport || user.role === "admin";

  // "RFQ & Costing" group items (children keep their existing routes/pages/icons).
  const rfqCostingItems = (col) => (
    <>
      {showNpd && (
        <NavItem to="/npd" icon={FileText} label="NPD Costings" testid="nav-npd" collapsed={col} active={onNpd && !pendingActive && !approvedActive} />
      )}
      {showAssembly && (
        <NavItem to="/assemblies" icon={Layers} label="Assemblies" testid="nav-assemblies" collapsed={col} />
      )}
      {showCostBreakdown && (
        <NavItem to="/cost-breakdown" icon={ReceiptText} label="Cost Breakdown" testid="nav-cost-breakdown" collapsed={col} />
      )}
    </>
  );

  // "APQP" group items — Projects, Master, Reports.
  const apqpItems = (col) => (
    <>
      <NavItem to="/apqp" icon={ListChecks} label="Projects" testid="nav-apqp-projects" collapsed={col} />
      {hasPermission("apqp.manage_master") && (
        <NavItem to="/apqp-master" icon={ClipboardCheck} label="APQP Master" testid="nav-apqp-master" collapsed={col} />
      )}
      <NavItem to="/apqp-reports" icon={BarChart3} label="APQP Reports" testid="nav-apqp-reports" collapsed={col} />
    </>
  );

  // "Masters" group items, gated per permission (rendered collapsed or expanded).
  const mastersItems = (col) => (
    <>
      {canManageCustomers && (
        <NavItem to="/customers" icon={Users} label="Customers" testid="nav-customers" collapsed={col} />
      )}
      {(canEditSettings || canEditProcesses) && (
        <NavItem to="/machines" icon={Cpu} label="Machines" testid="nav-machines" collapsed={col} />
      )}
      {showMaterials && (
        <NavItem to="/materials" icon={Boxes} label="Materials" testid="nav-materials" collapsed={col} />
      )}
      {showOperations && (
        <NavItem to="/operations" icon={Wrench} label="Operations" testid="nav-operations" collapsed={col} />
      )}
      {canManageRoles && (
        <NavItem to="/roles" icon={Shield} label="Roles" testid="nav-roles" collapsed={col} />
      )}
      {canViewRbacAudit && (
        <NavItem to="/audit" icon={ShieldCheck} label="RBAC Audit" testid="nav-audit" collapsed={col} />
      )}
      {(canEditSettings || canEditProcesses) && (
        <NavItem to="/settings" icon={Settings} label="Settings" testid="nav-settings" collapsed={col} />
      )}
      {canEditSettings && (
        <NavItem to="/tnc" icon={FileCheck} label="T&C Template" testid="nav-tnc" collapsed={col} />
      )}
    </>
  );

  // "Administration" group items, gated per permission.
  const adminItems = (col) => (
    <>
      {canManageUnits && (
        <NavItem to="/units" icon={Building2} label="Units (Plants)" testid="nav-units" collapsed={col} />
      )}
      {canManageUsers && (
        <NavItem to="/users" icon={Users} label="Users" testid="nav-users" collapsed={col} />
      )}
      {canManageApiKeys && (
        <NavItem to="/admin/api-keys" icon={KeyRound} label="API Keys" testid="nav-api-keys" collapsed={col} />
      )}
      {user.role === "admin" && (
        <NavItem to="/admin/rfq-orphans" icon={Wrench} label="RFQ Orphans" testid="nav-rfq-orphans" collapsed={col} />
      )}
      {canHrExport && (
        <NavItem to="/admin/hr-export" icon={CalendarClock} label="HR Export (Monthly)" testid="nav-hr-export" collapsed={col} />
      )}
      {canDeleteNpd && (
        <NavItem to="/trash" icon={Trash2} label="Trash" testid="nav-trash" collapsed={col} />
      )}
    </>
  );

  return (
    <aside
      className={`relative bg-slate-900 text-slate-200 min-h-screen flex flex-col flex-shrink-0 transition-[width] duration-200 ease-out ${
        collapsed ? "w-16" : "w-56"
      }`}
      data-testid="sidebar"
      data-collapsed={collapsed}
    >
      {/* Toggle chevron — sits half outside the right edge of the sidebar */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute top-4 -right-3 z-30 bg-white text-slate-700 border border-slate-200 rounded-full p-1 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-300"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Header — logo always shown, brand text hidden when collapsed */}
      <div className={`border-b border-slate-800 ${collapsed ? "p-3 flex justify-center" : "p-5"}`}>
        <div className={`flex items-center ${collapsed ? "" : "gap-2.5"}`}>
          <div
            className={`bg-white rounded-lg flex items-center justify-center p-1 flex-shrink-0 ${
              collapsed ? "w-10 h-10" : "w-12 h-12"
            }`}
            title={collapsed ? "AAMPL NPD" : undefined}
          >
            <img src="/aampl-logo.png" alt="AAMPL" className="w-full h-full object-contain" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-bold text-sm text-white leading-tight">AAMPL NPD</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">NPD Dashboard</div>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav
        className={`flex-1 overflow-y-auto space-y-0.5 ${collapsed ? "p-2" : "p-3"}`}
        data-testid="sidebar-nav"
      >
        {apqpOnly ? (
          /* APQP-only role — the sidebar shows just the APQP box. */
          <NavItem to="/apqp" icon={ListChecks} label="APQP Projects" testid="nav-apqp-projects" collapsed={collapsed} />
        ) : (
        <>
        {canSee("dashboard.view") && (
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" collapsed={collapsed} />
        )}
        {canSee("dashboard.view") && <AlertsBell collapsed={collapsed} />}
        {canSee("ballooning.view") && (
          <NavItem to="/ballooning" icon={Target} label="Ballooning" testid="nav-ballooning" collapsed={collapsed} />
        )}
        {canSee("ballooning.view") && (
          <NavItem to="/kc-test" icon={FlaskConical} label="KC Test Bench" testid="nav-kc-test" collapsed={collapsed} />
        )}
        {canSee("rfq.view") && (
          <NavItem to="/rfqs" icon={Inbox} label="RFQ Register" testid="nav-rfqs" collapsed={collapsed} />
        )}

        {/* RFQ & Costing — collapsible group (NPD Costings, Assemblies) */}
        {showCosting && (
        collapsed ? (
          <>
            {rfqCostingItems(true)}
          </>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setRfqCostingOpen((o) => !o)}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
              data-testid="nav-rfq-costing"
              aria-expanded={rfqCostingOpen}
            >
              <FolderKanban size={16} />
              <span className="flex-1 text-left">Costing</span>
              {rfqCostingOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {rfqCostingOpen && (
              <div className="ml-4 pl-2 border-l border-slate-800 space-y-0.5 mt-0.5" data-testid="nav-rfq-costing-items">
                {rfqCostingItems(false)}
              </div>
            )}
          </div>
        ))}

        {canApproveNpd && (
          <NavItem to="/npd?pending_approval=yes" icon={ClipboardCheck} label="Under Approval" testid="nav-under-approval" collapsed={collapsed} active={pendingActive} badge={pendingCount} iconClass="text-amber-400" />
        )}
        {canViewApproved && (
          <NavItem to="/npd?approved=yes" icon={CheckCircle2} label="Approved Quotes" testid="nav-approved-quotes" collapsed={collapsed} active={approvedActive} badge={approvedCount} iconClass="text-emerald-400" />
        )}
        {/* APQP — collapsible group (Projects, APQP Master, APQP Reports) */}
        {hasPermission("apqp.view") && (
          collapsed ? (
            <>{apqpItems(true)}</>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setApqpOpen((o) => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                data-testid="nav-apqp"
                aria-expanded={apqpOpen}
              >
                <ListChecks size={16} />
                <span className="flex-1 text-left">APQP Projects</span>
                {apqpOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {apqpOpen && (
                <div className="ml-4 pl-2 border-l border-slate-800 space-y-0.5 mt-0.5" data-testid="nav-apqp-items">
                  {apqpItems(false)}
                </div>
              )}
            </div>
          )
        )}
        {canViewReports && (
          <NavItem to="/reports" icon={BarChart3} label="Reports" testid="nav-reports" collapsed={collapsed} />
        )}
        {/* Masters — collapsible group (Customers, Materials, Operations, Roles, RBAC Audit, Settings, T&C) */}
        {showMasters && (
        collapsed ? (
          <>
            <div className="my-2 mx-1 border-t border-slate-700" aria-hidden="true" />
            {mastersItems(true)}
          </>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setMastersOpen((o) => !o)}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
              data-testid="nav-masters"
              aria-expanded={mastersOpen}
            >
              <Database size={16} />
              <span className="flex-1 text-left">Masters</span>
              {mastersOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {mastersOpen && (
              <div className="ml-4 pl-2 border-l border-slate-800 space-y-0.5 mt-0.5" data-testid="nav-masters-items">
                {mastersItems(false)}
              </div>
            )}
          </div>
        ))}

        {/* Administration — collapsible group (Units, Users, RFQ Orphans, Trash) */}
        {showAdminSection && (
          collapsed ? (
            <>
              <div className="my-2 mx-1 border-t border-slate-700" aria-hidden="true" />
              {adminItems(true)}
            </>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setAdminOpen((o) => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                data-testid="nav-administration"
                aria-expanded={adminOpen}
              >
                <Cog size={16} />
                <span className="flex-1 text-left">Administration</span>
                {adminOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {adminOpen && (
                <div className="ml-4 pl-2 border-l border-slate-800 space-y-0.5 mt-0.5" data-testid="nav-administration-items">
                  {adminItems(false)}
                </div>
              )}
            </div>
          )
        )}
        </>
        )}
      </nav>
    </aside>
  );
}

function NavItem({ to, icon: Icon, label, testid, collapsed, active, badge, iconClass }) {
  return (
    <NavLink
      to={to}
      end={to === "/dashboard"}
      data-testid={testid}
      title={collapsed ? label : undefined}
      className={({ isActive: navActive }) => {
        // `active` (when passed) overrides NavLink's path-only detection so two links
        // sharing a pathname (/npd) can resolve their active state via the query string.
        const isActive = active === undefined ? navActive : active;
        return `relative flex items-center rounded-md text-sm transition-colors ${
          collapsed ? "justify-center px-0 py-1.5" : "gap-2.5 px-3 py-1.5"
        } ${
          isActive
            ? collapsed
              ? "bg-orange-500/15 text-orange-300"
              : "bg-orange-500/15 text-orange-300 border-l-2 border-orange-500 pl-2.5"
            : "text-slate-300 hover:bg-slate-800 hover:text-white"
        }`;
      }}
    >
      <Icon size={16} className={iconClass} />
      {!collapsed && <span className="flex-1">{label}</span>}
      {!collapsed && badge > 0 && (
        <span className="text-[10px] font-bold bg-orange-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{badge > 99 ? "99+" : badge}</span>
      )}
      {collapsed && badge > 0 && (
        <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-orange-500" />
      )}
    </NavLink>
  );
}
